import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@expertloop/i18n/react";
import { useAuthStore } from "../stores/authStore";

export function LoginPage() {
	const { t } = useI18n();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState("");
	const [requires2FA, setRequires2FA] = useState(false);
	const [tempToken, setTempToken] = useState("");
	const [twoFACode, setTwoFACode] = useState("");
	const [useRecoveryCode, setUseRecoveryCode] = useState(false);

	const login = useAuthStore((s) => s.login);
	const verify2FA = useAuthStore((s) => s.verify2FA);
	const isLoading = useAuthStore((s) => s.isLoading);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setError("");

		try {
			const result = await login(email, password);
			if (result?.requires2FA && result.tempToken) {
				setRequires2FA(true);
				setTempToken(result.tempToken);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : t("auth.login.failed"));
		}
	};

	const handle2FASubmit = async (e: FormEvent) => {
		e.preventDefault();
		setError("");

		try {
			await verify2FA(tempToken, twoFACode, useRecoveryCode);
		} catch (err) {
			setError(err instanceof Error ? err.message : t("adminLogin.verificationFailed"));
		}
	};

	const handleBackToLogin = () => {
		setRequires2FA(false);
		setTempToken("");
		setTwoFACode("");
		setError("");
		setUseRecoveryCode(false);
	};

	if (requires2FA) {
		return (
			<div className="login-page">
				<div className="login-card">
					<h1>{t('adminLogin.twoFactorTitle')}</h1>
					<p className="subtitle">
						{useRecoveryCode
							? t('adminLogin.enterRecoveryCode')
							: t('adminLogin.enterAuthenticatorCode')}
					</p>

					{error && <div className="error-message">{error}</div>}

					<form onSubmit={handle2FASubmit}>
						<div className="form-group">
							<label htmlFor="twoFACode">
								{useRecoveryCode ? t('adminLogin.recoveryCode') : t('adminLogin.verificationCode')}
							</label>
							<input
								id="twoFACode"
								type="text"
								value={twoFACode}
								onChange={(e) => {
									if (useRecoveryCode) {
										setTwoFACode(e.target.value.toUpperCase());
									} else {
										setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6));
									}
								}}
								placeholder={useRecoveryCode ? "XXXX-XXXX" : "000000"}
								required
								autoFocus
								className={useRecoveryCode ? "" : "code-input"}
							/>
						</div>

						<button
							type="submit"
							className="btn btn-primary"
							disabled={isLoading || (!useRecoveryCode && twoFACode.length !== 6)}
						>
							{isLoading ? t('adminLogin.verifying') : t('adminLogin.verify')}
						</button>
					</form>

					<div className="two-fa-options">
						<button
							type="button"
							className="link-button"
							onClick={() => {
								setUseRecoveryCode(!useRecoveryCode);
								setTwoFACode("");
								setError("");
							}}
						>
							{useRecoveryCode
								? t('adminLogin.useAuthenticatorInstead')
								: t('adminLogin.useRecoveryCode')}
						</button>
						<button
							type="button"
							className="link-button"
							onClick={handleBackToLogin}
						>
							{t('adminLogin.backToLogin')}
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="login-page">
			<div className="login-card">
				<h1>{t("admin.login.title")}</h1>
				<p className="subtitle">{t("admin.login.subtitle")}</p>

				{error && <div className="error-message">{error}</div>}

				<form onSubmit={handleSubmit}>
					<div className="form-group">
						<label htmlFor="email">{t("auth.login.email")}</label>
						<input
							id="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder={t("admin.login.emailPlaceholder")}
							required
						/>
					</div>

					<div className="form-group">
						<label htmlFor="password">{t("auth.login.password")}</label>
						<input
							id="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder={t("auth.login.passwordPlaceholder")}
							required
						/>
					</div>

					<button type="submit" className="btn btn-primary" disabled={isLoading}>
						{isLoading ? t("admin.login.submitting") : t("admin.login.submit")}
					</button>
				</form>

				<div className="login-footer">
					<Link to="/forgot-password" className="link-button">
						{t("adminLogin.forgotPassword")}
					</Link>
				</div>
			</div>
		</div>
	);
}
